/**
 * Componente de alerta modal.
 * 
 * Props principales:
 * - isOpen (bool): Controla si la alerta está visible.
 * - type (string): "success" | "error" | "warning" | "question".
 * - title (string): Título principal de la alerta.
 * - description (string): Texto descriptivo o mensaje de la alerta.
 * - showOkButton (bool): Muestra/oculta el botón "OK".
 * - showRemoveButton (bool): Muestra/oculta el botón "Remove".
 * - showCancelButton (bool): Muestra/oculta el botón "Cancel".
 * - okButtonText (string): Texto del botón OK.
 * - removeButtonText (string): Texto del botón Remove.
 * - cancelButtonText (string): Texto del botón Cancel.
 * - autoClose (bool): Cierra automáticamente la alerta después de "timer" milisegundos.
 * - timer (number): Milisegundos para el cierre automático.
 * - onOk, onRemove, onCancel, onClose: Callbacks para manejar eventos de cada botón y/o cierre.
 */
import React, { useEffect } from "react";

const typeConfig = {
	success: {
		icon: "far fa-check-circle",
		color: "#4caf50",
	},
	error: {
		icon: "fas fa-exclamation-triangle",
		color: "#f44336",
	},
	warning: {
		icon: "fas fa-exclamation-circle",
		color: "#E8B600",
	},
	question: {
		icon: "far fa-question-circle",
		color: "#2196f3",
	},
};

export default function SourAlert({
	isOpen = false,
	type = "success",
	title = "",
	description = "",
	showOkButton = true,
	showRemoveButton = false,
	showCancelButton = false,
	okButtonText = "OK",
	removeButtonText = "Remove",
	cancelButtonText = "Cancel",
	autoClose = false,
	timer = 3000,
	onOk = () => {},
	onRemove = () => {},
	onCancel = () => {},
	onClose = () => {},
}) {
	useEffect(() => {
		let timeoutId;
		if (isOpen && autoClose) {
			timeoutId = setTimeout(() => {
				onClose();
			}, timer);
		}
		return () => timeoutId && clearTimeout(timeoutId);
	}, [isOpen, autoClose, timer, onClose]);

	if (!isOpen) return null;

	const { icon, color } = typeConfig[type] || typeConfig.success;

	const handleOk = () => {
		onOk();
		onClose();
	};
	const handleRemove = () => {
		onRemove();
		onClose();
	};
	const handleCancel = () => {
		onCancel();
		onClose();
	};

	return (
		<div className="sour-alert">
			<div className="modal">
				<div
					className="icon-wrapper"
					style={{ backgroundColor: `${color}20` }}
				>
					<i className={`${icon} icon`} style={{ color }} />
				</div>
				<h2 className="title">{title}</h2>
				<div
					className="divider"
					style={{
						backgroundColor: color,
						height: "3px",
						width: "80px",
						margin: "10px auto",
					}}
				/>
				<p className="description">{description}</p>
				<div className="buttons">
					{showRemoveButton && (
						<button className="remove" onClick={handleRemove}>
							{removeButtonText}
						</button>
					)}
					{showCancelButton && (
						<button className="cancel" onClick={handleCancel}>
							{cancelButtonText}
						</button>
					)}
					{showOkButton && (
						<button className="ok" onClick={handleOk}>
							{okButtonText}
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
